import { z } from 'zod';
import {
  billingPeriods,
  subscriptionPlans,
  type BillingPeriod,
  type SubscriptionPlan,
  type SubscriptionStatus,
} from '../enums.js';

/**
 * Abonnement d'un cabinet.
 *
 * **Le cabinet est l'unité d'abonnement**, pas l'utilisateur : la V1 portait l'offre sur le compte,
 * ce qui n'a plus de sens dès qu'un praticien appartient à deux cabinets.
 *
 * Aucune donnée de carte ne franchit ce contrat, ni dans un sens ni dans l'autre. Le passage en
 * caisse et la mise à jour d'un moyen de paiement se font sur des **pages hébergées par Stripe** :
 * nos serveurs n'en voient jamais les champs, ce qui nous sort du périmètre PCI et confie
 * l'authentification forte, obligatoire en Europe, à qui sait la faire. Les deux routes qui les
 * ouvrent ne rendent donc qu'une URL.
 */

/* -------------------------------------------------------------------------- */
/* Tarifs                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Les offres qui se paient. Toutes, depuis le retrait de l'offre gratuite : un cabinet entre par
 * un essai de 30 jours, puis souscrit l'une des trois.
 */
export const payablePlans = subscriptionPlans;

/**
 * Nom du tarif Stripe correspondant à une offre et une périodicité.
 *
 * **C'est cette clé, et jamais un identifiant `price_…`, qui relie notre catalogue au leur.** Un
 * identifiant de tarif est propre à un compte Stripe : le coller dans une variable
 * d'environnement, c'est se garantir qu'un jour le test et la production pointeront le même, ou
 * deux tarifs qui ne disent pas le même prix. Une `lookup_key` se retrouve par l'API, vaut pour
 * tous les comptes, et le script de catalogue la pose.
 *
 * `annual` devient `yearly` parce que c'est le mot de Stripe ; la traduction est ici, une fois.
 */
export function stripeLookupKey(plan: SubscriptionPlan, period: BillingPeriod): string {
  return `osteopro_${plan}_${period === 'annual' ? 'yearly' : 'monthly'}`;
}

/** Toutes les clés du catalogue payant : trois offres × deux périodicités. */
export const stripeLookupKeys: readonly string[] = payablePlans.flatMap((plan) =>
  billingPeriods.map((period) => stripeLookupKey(plan, period)),
);

/* -------------------------------------------------------------------------- */
/* Lecture                                                                     */
/* -------------------------------------------------------------------------- */

export interface SubscriptionDto {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  period: BillingPeriod;
  /** Fin de la période payée. Nulle sur l'offre gratuite, qui ne se renouvelle pas. */
  currentPeriodEnd: string | null;
  /**
   * L'abonnement est-il porté par Stripe ? Faux pour un cabinet dont l'offre a été posée à la
   * main par la console — la voie « facturé hors application », qui reste ouverte.
   */
  managedByStripe: boolean;
  /**
   * Résiliation demandée, effective à la fin de la période. L'écran doit dire « jusqu'au … »
   * plutôt que « actif », sans quoi le praticien découvre la coupure le jour où elle tombe.
   */
  cancelAtPeriodEnd: boolean;
  /** Le portail client est-il ouvrable ? Faux tant que le cabinet n'a jamais payé. */
  portalAvailable: boolean;
  /** Stripe est-il configuré sur cette installation ? Faux, aucune route de caisse ne répond. */
  configured: boolean;
}

/* -------------------------------------------------------------------------- */
/* Passage en caisse                                                           */
/* -------------------------------------------------------------------------- */

export const subscriptionCheckoutSchema = z
  .object({
    plan: z.enum(subscriptionPlans),
    period: z.enum(billingPeriods),
  })
  .strict();
export type SubscriptionCheckoutInput = z.infer<typeof subscriptionCheckoutSchema>;

/** Une URL, et rien d'autre : la page est chez Stripe, le navigateur y va. */
export interface StripeRedirectDto {
  url: string;
}

/**
 * Résultat de « Choisir cette offre ».
 *
 * `checkout` : pas d'abonnement vivant, le navigateur part en caisse.
 * `updated` : offre plus chère, la différence est facturée tout de suite. Pas de seconde caisse.
 * `scheduled` : offre moins chère, le changement attend la fin de la période déjà payée.
 * `current` : le cabinet a déjà exactement cette offre et cette période. Rien n'est prélevé.
 */
export interface SubscriptionCheckoutResultDto {
  outcome: 'checkout' | 'updated' | 'scheduled' | 'current';
  /** Renseignée seulement pour `checkout`. Nulle quand on reste dans l'application. */
  url: string | null;
  /**
   * Fin de la période déjà payée, seulement pour `scheduled`. L'offre en cours continue
   * jusqu'à cette date. Nulle si le prestataire ne l'a pas dite.
   */
  effectiveAt: string | null;
}

/* -------------------------------------------------------------------------- */
/* Supervision, côté plateforme                                                */
/* -------------------------------------------------------------------------- */

export interface StripePlanBreakdownDto {
  plan: SubscriptionPlan;
  period: BillingPeriod;
  subscriptions: number;
}

/**
 * Ce que la console montre de Stripe. `configured: false` est une réponse complète et normale :
 * l'installation n'a pas de clé, il n'y a rien à afficher et rien ne casse.
 */
export interface PlatformStripeDto {
  configured: boolean;
  /** Abonnements actifs par offre et par périodicité. Des **nombres**, jamais des montants. */
  breakdown: StripePlanBreakdownDto[];
  /** Abonnements en impayé. L'accès reste ouvert ; c'est une alerte d'exploitation. */
  pastDue: number;
  /** Résiliations demandées et pas encore effectives. */
  cancelling: number;
  /**
   * Clients Stripe portant **plus d'un** abonnement en cours.
   *
   * Zéro est l'état normal et le seul acceptable : un cabinet est une unité d'abonnement. Un
   * nombre non nul veut dire qu'un cabinet est prélevé deux fois — c'est arrivé le 21 septembre
   * sur le compte de test, la caisse ne refusant alors que le rachat de la **même** offre. Le
   * compte reste un nombre, comme tout le reste de cet écran : le détail se lit chez Stripe.
   */
  duplicates: number;
  /** Prochain renouvellement, tous cabinets confondus. Nul si aucun abonnement n'est en cours. */
  nextRenewalAt: string | null;
}
